import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import styles from '../../../Configurations.module.css';
import componentStyles from './ContactTypes.module.css';
import { contactTypeService } from '../../../../services/contactTypeService';
import { useAlert } from '../../../../context/AlertContext';
import { useAccessToken } from '../../../../hooks/useAuth';
import ContactTypeForm from './ContactTypeForm';
import ContactTypeList from './ContactTypeList';
import ContactTypeStatistics from './ContactTypeStatistics';

const ContactTypes = () => {
    const { setErrorAlert, setInfoAlert, setIsModalOpen } = useAlert();
    const { getBackendAccessToken } = useAccessToken();
    const [contactTypes, setContactTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statistics, setStatistics] = useState({
        total: 0
    });

    const [showForm, setShowForm] = useState(false);
    const [editingType, setEditingType] = useState(null);

    useEffect(() => {
        loadContactTypes();
    }, []);

    // Track modal state for alert positioning and header blocking
    useEffect(() => {
        if (showForm) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [showForm, setIsModalOpen]);

    const loadContactTypes = async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            const data = await contactTypeService.getAll(accessToken);
            
            if (Array.isArray(data)) {
                setContactTypes(data);
                setError(null);
                // Update statistics
                setStatistics({
                    total: data.length
                });
            } else {
                throw new Error(`Invalid response format. Expected array, got ${typeof data}`);
            }
        } catch (err) {
            const errorMessage = 'Failed to load contact types. Please try again later.';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error loading contact types:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            const accessToken = await getBackendAccessToken();
            if (editingType) {
                await contactTypeService.update(accessToken, editingType.contactTypeId, formData);
                setInfoAlert('Contact type updated successfully');
            } else {
                await contactTypeService.create(accessToken, formData);
                setInfoAlert('Contact type created successfully');
            }
            await loadContactTypes();
            setShowForm(false);
            setEditingType(null);
            setError(null);
        } catch (err) {
            setError(err.message || 'Operation failed');
            setErrorAlert(err.message || 'Operation failed');
            console.error('Error handling contact type:', err);
        }
    };

    const handleEdit = (type) => {
        setEditingType(type);
        setShowForm(true);
    };

    const handleDelete = async (contactTypeId) => {
        if (!window.confirm('Are you sure you want to delete this contact type?')) {
            return;
        }

        try {
            const accessToken = await getBackendAccessToken();
            await contactTypeService.delete(accessToken, contactTypeId);
            await loadContactTypes();
            setInfoAlert('Contact type deleted successfully');
            setError(null);
        } catch (err) {
            const errorMsg = 'Failed to delete contact type. Please try again.';
            setError(errorMsg);
            setErrorAlert(errorMsg);
            console.error('Error deleting contact type:', err);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingType(null);
    };

    const filteredContactTypes = contactTypes.filter(type => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            type.contactType.toLowerCase().includes(query) ||
            (type.description && type.description.toLowerCase().includes(query))
        );
    });

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Contact Types</h2>
                <p>Manage and configure different types of contact methods in the system.</p>
            </div>

            <div className={styles.content}>
                {/* Statistics Section */}
                {!loading && !error && (
                    <ContactTypeStatistics 
                        statistics={statistics}
                        loading={loading}
                    />
                )}

                {error && (
                    <div className={componentStyles.errorMessage}>
                        <span className={componentStyles.errorIcon}>⚠</span>
                        {error}
                    </div>
                )}

                {/* Controls Section */}
                <div className={componentStyles.controls}>
                    <button
                        onClick={() => setShowForm(true)}
                        className={componentStyles.addButton}
                        disabled={showForm || loading}
                    >
                        <FaPlus className={componentStyles.buttonIcon} /> Create New Contact Type
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className={componentStyles.modal}>
                        <div className={componentStyles.modalContent}>
                            <ContactTypeForm
                                contactType={editingType}
                                onSubmit={handleFormSubmit}
                                onCancel={handleCancel}
                                loading={loading}
                            />
                        </div>
                    </div>
                )}

                {/* Contact Types List */}
                <ContactTypeList
                    contactTypes={filteredContactTypes}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
            </div>
        </div>
    );
};

export default ContactTypes;
